import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link, Navigate } from "react-router-dom";
import { Building2, ArrowRight } from "lucide-react";
import { Navigation } from "@/components/ui/navigation";

export default function CompanySelect() {
  const { user, companies, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto py-8">
          <div className="text-center">Carregando empresas...</div>
        </div>
      </div>
    );
  }

  // If no user, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user has only one company, redirect directly
  if (companies.length === 1) {
    const company = companies[0];
    return <Navigate to={`/company/${company.slug}/user`} replace />;
  }

  // If user has no companies, show message
  if (companies.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto py-8">
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle>Acesso Negado</CardTitle>
              <CardDescription>
                Sua conta não possui acesso a nenhuma empresa no PortalONE.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Entre em contato com o suporte para solicitar acesso.
              </p>
              <Button asChild className="w-full">
                <Link to="/contact">
                  Entrar em Contato
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="container mx-auto py-8 max-w-2xl">
        <div className="text-center space-y-4 mb-8">
          <h1 className="text-3xl font-bold tracking-tight">
            Selecionar Empresa
          </h1>
          <p className="text-lg text-muted-foreground">
            Você tem acesso a múltiplas empresas. Selecione uma para continuar ao dashboard.
          </p>
        </div>

        <div className="grid gap-4">
          {companies.map((company) => (
            <Card 
              key={company.id} 
              className="hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/20"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">{company.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        Status: {company.status === 'active' ? 'Ativa' : 'Inativa'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={company.role === 'company_admin' ? 'default' : 'secondary'}>
                      {company.role === 'company_admin' ? 'Administrador' : 'Usuário'}
                    </Badge>
                    <Button asChild>
                      <Link to={`/company/${company.slug}/user`}>
                        Acessar
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-8">
          <p className="text-sm text-muted-foreground">
            Não encontra a empresa que procura?{' '}
            <Link to="/contact" className="text-primary hover:underline">
              Entre em contato conosco
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}